/**
 * request-session store
 */
import { create } from 'zustand';
import { sessionListPost, sessionUnreadPost, sessionInfoPost } from '@/services/swagger/easourcing/SessionService';
import { IApiSessionDto } from '@/services/swagger/easourcing/models';

interface RequestSessionState {
  lastId: string | undefined;
  lastModifyTime: number | undefined;
  lastPinnedTop: number | undefined;
  requestSessionList: IApiSessionDto[];
  //未读数据
  unreadStatusMap: Record<string, number>;
  // 总未读数
  totalUnreadCount: number;
  loading: boolean;
  _isPending: boolean;
  hasMore: boolean;
  pageSize: number;
  scrollOffset: number;
  loadSessionList: (showLoading?: boolean, overrideList?: boolean) => Promise<void>;
  reloadSessionList: (showLoading?: boolean, clearData?: boolean) => Promise<void>;
  setScrollOffset: (offset: number) => void;
  updateSessionItem: (sessionId: string, data?: Partial<IApiSessionDto>) => void;
  loadUnreadStatus: (sessionIdList: string[], onlyTotalUnread?: boolean) => Promise<void>;
  updateUnreadStatusMap: (contactId: string, count: number) => void;
  updateTotalUnreadCount: (count: number) => void;
  moveSessionToTop: (sessionId: string) => void;
}

// 创建 store
const useStore = create<RequestSessionState>((set, get) => ({
  lastId: undefined,
  lastModifyTime: undefined,
  lastPinnedTop: undefined,
  requestSessionList: [],
  unreadStatusMap: {},
  scrollOffset: 0,
  totalUnreadCount: 0,
  loading: true,
  _isPending: false,
  hasMore: true,
  pageSize: 100,
  setScrollOffset: (offset: number) => set({ scrollOffset: offset }),
  loadSessionList: async (showLoading?: boolean, overrideList?: boolean) => {
    try {
      const {
        lastId,
        lastModifyTime,
        pageSize,
        requestSessionList,
        _isPending,
        hasMore,
        loadUnreadStatus,
        lastPinnedTop,
      } = get();
      if (_isPending || !hasMore) {
        return;
      }

      if (showLoading) {
        set({ loading: true });
      }
      set({ _isPending: true });
      const res = await sessionListPost({
        lastId: lastId,
        lastModifyTime: lastModifyTime,
        lastPinnedTop: lastPinnedTop,
        pageSize,
      });

      const newList = !overrideList
        ? requestSessionList.concat(res?.data?.sessionList || [])
        : res?.data?.sessionList || [];

      set({
        requestSessionList: newList,
        lastId: res?.data?.lastId,
        lastModifyTime: res?.data?.lastModifyTime,
        hasMore: res?.data?.sessionList.length === pageSize,
        lastPinnedTop: res?.data?.lastPinnedTop,
      });
      loadUnreadStatus((res?.data?.sessionList || []).map(item => item.sessionId));
      set({ _isPending: false });
    } catch {
      set({ _isPending: false });
    } finally {
      set({ loading: false });
    }
  },
  reloadSessionList: async (showLoading?: boolean, clearData?: boolean) => {
    try {
      const { loadSessionList, _isPending } = get();
      if (_isPending) {
        return;
      }

      if (clearData) {
        set({
          requestSessionList: [],
          unreadStatusMap: {},
        });
      }

      set({
        lastId: undefined,
        lastModifyTime: undefined,
        lastPinnedTop: undefined,
        hasMore: true,
        scrollOffset: 0,
      });
      await loadSessionList(showLoading, true);
    } catch {
      console.error('Failed to reload session list');
    }
  },
  updateSessionItem: (sessionId: string, data?: Partial<IApiSessionDto>) => {
    if (!data) {
      // 如果没有数据需要更新，访问接口获取最新信息
      sessionInfoPost({ sessionId }).then(res => {
        const sessionData = res?.data?.sessionDto;
        if (sessionData) {
          set(state => ({
            requestSessionList: state.requestSessionList.map(item =>
              item.sessionId === sessionData?.sessionId ? { ...item, ...sessionData } : item
            ),
          }));
        }
      });
      return;
    }

    set(state => ({
      requestSessionList: state.requestSessionList.map(item =>
        item.sessionId === sessionId ? { ...item, ...data } : item
      ),
    }));
  },
  loadUnreadStatus: async (sessionIdList: string[], onlyTotalUnread?: boolean) => {
    try {
      const res = await sessionUnreadPost({ sessionIdList, allUnread: true });
      const unreadStatusMap: Record<string, number> = {};
      res?.data?.sessionUnreadList?.forEach(item => {
        unreadStatusMap[item.sessionId] = item.unreadNum || 0;
      });
      if (onlyTotalUnread) {
        set({ totalUnreadCount: res?.data?.totalUnreadNum || 0 });
        return;
      }
      set({
        unreadStatusMap: {
          ...get().unreadStatusMap,
          ...unreadStatusMap,
        },
        totalUnreadCount: res?.data?.totalUnreadNum || 0,
      });
    } catch {
      console.error('Failed to load unread status');
    }
  },
  updateUnreadStatusMap: (contactId: string, count: number) => {
    set(state => ({
      unreadStatusMap: {
        ...state.unreadStatusMap,
        [contactId]: count,
      },
    }));
  },
  updateTotalUnreadCount: (count: number) => {
    set({ totalUnreadCount: count });
  },
  moveSessionToTop: (sessionId: string) => {
    set(state => {
      const sessionIndex = state.requestSessionList.findIndex(item => item.sessionId === sessionId);
      if (sessionIndex === -1) return {};
      const newList = [...state.requestSessionList];
      const [movedSession] = newList.splice(sessionIndex, 1);

      // 同步判断 movedSession.pinnedTop  如果pinnedTop，移动到最顶部，否则移动到第一个非pinnedTop的位置
      if (movedSession.pinnedTop) {
        newList.unshift(movedSession);
      } else {
        const firstNonPinnedIndex = newList.findIndex(item => !item.pinnedTop);
        if (firstNonPinnedIndex === -1) {
          newList.push(movedSession);
        } else {
          newList.splice(firstNonPinnedIndex, 0, movedSession);
        }
      }
      return { requestSessionList: newList };
    });
  },
}));

export default useStore;
